import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/* One member's ad, for members it fits. Clearly labelled, never in Copilot,
   intros or messages. A view counts only once the card is actually on screen. */

const REPORT_REASONS = [
  ['spam', 'Spam or misleading'],
  ['scam', 'Scam or fraud'],
  ['other', 'Something else'],
];

export default function SponsoredCard({ notify, onGo, embedded = false, onLoaded }) {
  const [ad, setAd] = useState(null);
  const [code, setCode] = useState(null);
  const [why, setWhy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const counted = useRef('');
  const loaded = useRef(onLoaded);
  loaded.current = onLoaded;

  useEffect(() => {
    api.adServe().then((a) => { setAd(a); loaded.current?.(Boolean(a)); }).catch(() => { setAd(null); loaded.current?.(false); });
  }, []);

  useEffect(() => {
    if (!ad || !ref.current || counted.current === ad.id) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) {
        counted.current = ad.id;
        api.adSeen(ad.id).catch(() => {});
        io.disconnect();
      }
    }, { threshold: [0.5] });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ad]);

  if (!ad) return null;

  async function run(fn) {
    setBusy(true);
    try { await fn(); } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }

  const visit = () => run(async () => {
    const r = await api.adVisit(ad.id);
    window.open(r.url, '_blank', 'noopener,noreferrer');
  });
  const claim = () => run(async () => {
    const r = await api.adClaim(ad.id);
    setCode(r.code || '');
    if (!r.code) window.open(r.url, '_blank', 'noopener,noreferrer');
  });
  const talk = () => run(async () => {
    await api.adTalk(ad.id);
    notify?.(`Asked the ${ad.name} team. If they say yes too, you'll be connected under People.`);
  });
  const hide = () => run(async () => {
    await api.adHide(ad.id);
    setAd(null);
    notify?.("Hidden. You won't see this one again.");
  });
  const report = (reason) => run(async () => {
    await api.adReport(ad.id, reason);
    setAd(null);
    notify?.("Thanks — we'll take a look. You won't see this one again.");
  });

  return (
    <section className={embedded ? 'sponsored embedded' : 'card pad sponsored'} ref={ref} aria-label={`Sponsored: ${ad.name}`}>
      <div className="sp-head">
        <span className="sp-label">Sponsored · member perk</span>
        <button className="linklike sp-why" onClick={() => setWhy(!why)}>why?</button>
      </div>
      <div className="sp-body">
        {ad.logo_url ? <img className="sp-logo" src={ad.logo_url} alt="" /> : <span className="sp-logo sp-initial">{ad.name[0]}</span>}
        <div>
          <b>{ad.name}</b>
          <p className="sp-pitch">{ad.pitch}</p>
          {ad.perk && <p className="sp-perk">🎁 {ad.perk}</p>}
          <span className="muted-sm">by {ad.by} · {ad.domain}</span>
        </div>
      </div>
      {why && <p className="muted-sm sp-whytext">{ad.why} Nothing about you is shared with advertisers.</p>}
      {code !== null && code !== '' && (
        <div className="sp-code">
          <span className="muted-sm">Your code</span>
          <code>{code}</code>
          <button className="btn quiet sm" onClick={() => { navigator.clipboard?.writeText(code); notify?.('Code copied'); }}>Copy</button>
          <button className="btn quiet sm" onClick={visit}>Use it on {ad.domain}</button>
        </div>
      )}
      <div className="sp-actions">
        {ad.perk && code === null && <button className="btn primary sm" disabled={busy} onClick={claim}>Claim perk</button>}
        <button className="btn ghost sm" disabled={busy} onClick={talk}>Talk to the team</button>
        <button className="btn quiet sm" disabled={busy} onClick={visit}>Visit</button>
      </div>
      <div className="sp-foot">
        <button className="linklike" onClick={hide}>Hide</button>
        {!reporting ? <button className="linklike" onClick={() => setReporting(true)}>Report</button> : (
          REPORT_REASONS.map(([k, t]) => <button key={k} className="linklike" onClick={() => report(k)}>{t}</button>)
        )}
        <button className="linklike" onClick={() => onGo?.('promote')}>Promote your product</button>
      </div>
    </section>
  );
}
